import React from 'react';
import Modal from 'react-modal';

export default function DashboardHelpModal({ isOpen, onOpen, onClose, styles }) {
  return (
    <>
      <button className={styles.helpButton} onClick={onOpen} aria-label="Help">?</button>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        className={styles.helpModalContent}
        overlayClassName={styles.helpModalOverlay}
        contentLabel="Help Information"
      >
        <button onClick={onClose} className={styles.closeHelpButton}>Close</button>
        <div className={styles.helpText}>
          <h2>About this Dashboard</h2>
          <p>
            This dashboard provides an overview of system performance, production data, and failure analysis to help you monitor and evaluate testing processes effectively. Below are the main features and functionalities:
          </p>

          <h3>Key Features</h3>
          <ul>
            <li>
              <strong>System Overview:</strong> Displays real-time system health, including test success rates and failure counts.
            </li>
            <li>
              <strong>Performance Metrics:</strong> Provides detailed charts showing system performance over different periods, including success rates, failure trends, and overall system efficiency.
            </li>
            <li>
              <strong>Failure Analysis:</strong> Identifies critical areas and test steps where failures are most frequent, helping prioritize troubleshooting efforts.
            </li>
            <li>
              <strong>Interactive Charts:</strong> Includes various visualizations (bar charts, pie charts, Pareto charts) to highlight insights into system health and performance.
            </li>
          </ul>

          <h3>Filters</h3>
          <p>Customize the data displayed using the following filters:</p>
          <ul>
            <li>
              <strong>Date Range Filter:</strong> Customize the displayed data by selecting a specific time period (e.g., daily, monthly, quarterly).
            </li>
            <li>
              <strong>Version Filter:</strong> Filter performance data based on specific versions of the testing software (ATE SW Version).
            </li>
            <li>
              <strong>Serial Numbers Filter:</strong> Analyze test results for specific hardware units by filtering serial numbers.
            </li>
          </ul>

          <h3>Data Visualization</h3>
          <p>The dashboard provides a variety of visual elements to analyze test data:</p>
          <ul>
            <li>
              <strong>Pie Chart:</strong> Displays the distribution of PASS and FAIL test results, offering a quick summary of test outcomes.
            </li>
            <li>
              <strong>Pareto Chart:</strong> Visualizes failure counts and cumulative failure percentages by step, highlighting critical areas for improvement.
            </li>
            <li>
              <strong>Production Totals:</strong> Offers a summary of total successful tests and production output.
            </li>
            <li>
              <strong>Monthly Failure Rate:</strong> The Monthly Failure Rate chart shows the percentage of failed tests grouped by month over the selected time period.
            </li>
          </ul>

          <h3>Alarm System Button</h3>
          <p>
            The <strong>Root Cause Alarm</strong> button provides direct access to the <strong>Root Cause Alarm System</strong>. This page highlights high-priority failure relationships and offers troubleshooting tips for critical test steps.
          </p>
          <ul>
            <li>
              <strong>Flashing Red Button:</strong> The alarm button flashes red to indicate the presence of high-priority failures requiring immediate attention.
            </li>
            <li>
              <strong>Root Cause Analysis:</strong> Provides a list of test steps and their related failures with actionable troubleshooting insights.
            </li>
          </ul>

          <h3>System Health Status</h3>
          <p>The system health section displays the current state of the system:</p>
          <ul>
            <li><strong>Green Indicator:</strong> A green light shows the system is functioning smoothly.</li>
            <li><strong>Yellow Indicator:</strong> A yellow light indicates minor warnings that do not significantly impact performance.</li>
            <li><strong>Red Indicator:</strong> A red light signals critical issues that need immediate resolution.</li>
          </ul>

          <h3>Navigation</h3>
          <p>Use the navigation panel to access other sections of the dashboard:</p>
          <ul>
            <li><strong>File Upload:</strong> Upload new test data for analysis.</li>
            <li><strong>File Management:</strong> Manage and review uploaded files with options for editing or deleting.</li>
            <li><strong>Charts and Graphs:</strong> Explore detailed visualizations of system performance, trends, and failure analysis.</li>
          </ul>

          <p>
            This dashboard is designed to help you monitor production outcomes, troubleshoot failures, and improve overall system performance efficiently.
          </p>
        </div>
      </Modal>
    </>
  );
}
